import { useQueryClient } from "@tanstack/react-query"
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { getAccessToken, getApiUrl, refreshSession } from "../api/client"
import { useAuth } from "../auth/AuthContext"
import { useFamily } from "../family/FamilyContext"

type ConnectionState = "connecting" | "live" | "offline"

interface RealtimeContextValue {
  state: ConnectionState
}

const RealtimeContext = createContext<RealtimeContextValue>({ state: "connecting" })

interface Envelope {
  id?: string
  type: string
  sequence?: string | null
  payload?: unknown
}

function parseEvent(block: string): Envelope | null {
  const data = block
    .split(/\r?\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n")
  if (!data) return null
  try {
    const parsed = JSON.parse(data) as Partial<Envelope>
    return typeof parsed.type === "string" ? (parsed as Envelope) : null
  } catch {
    return null
  }
}

function relatedKeys(type: string): string[][] {
  if (type.startsWith("currency.") || type.startsWith("penalty.") || type.startsWith("ledger.")) {
    return [["currencies"], ["history"], ["notifications"]]
  }
  if (type.startsWith("achievement.")) return [["achievements"], ["achievement-awards"], ["history"], ["notifications"]]
  if (type.startsWith("task.")) return [["tasks"], ["activity"], ["actions"], ["notifications"]]
  if (type.startsWith("ability.")) return [["abilities"], ["activity"], ["actions"], ["notifications"]]
  if (type.startsWith("shop.")) return [["offers"], ["abilities"], ["currencies"], ["activity"]]
  if (type.startsWith("redeem.")) return [["currencies"], ["activity"], ["actions"], ["notifications"]]
  if (type.startsWith("scheduled-grant.")) return [["scheduled-grants"], ["activity"]]
  if (type.startsWith("item.")) return [["items"], ["inventory"], ["item-uses"], ["history"], ["notifications"]]
  if (
    type.startsWith("member.") ||
    type.startsWith("invitation.") ||
    type.startsWith("family.member.") ||
    type.startsWith("family.invitation.")
  )
    return [["members"], ["invitations"], ["notifications"]]
  if (type.startsWith("security.")) return [["members"], ["notifications"]]
  if (type.startsWith("notification.")) return [["notifications"]]
  if (type.startsWith("automation.")) return [["automations"]]
  return []
}

function cursorStorageKey(userId: string, familyId: string) {
  return `family-coin.realtime-cursor:${userId}:${familyId}`
}

function savedCursor(userId: string, familyId: string) {
  try {
    return sessionStorage.getItem(cursorStorageKey(userId, familyId))
  } catch {
    return null
  }
}

function saveCursor(userId: string, familyId: string, cursor: string) {
  try {
    sessionStorage.setItem(cursorStorageKey(userId, familyId), cursor)
  } catch {
    // Realtime still works when Safari private mode denies storage.
  }
}

export function RealtimeBridge({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const { activeFamilyId } = useFamily()
  const queryClient = useQueryClient()
  const [state, setState] = useState<ConnectionState>(navigator.onLine ? "connecting" : "offline")
  const [celebration, setCelebration] = useState<string | null>(null)

  useEffect(() => {
    if (!user || !activeFamilyId) return
    const controller = new AbortController()
    let cursor: string | null = savedCursor(user.id, activeFamilyId)
    let retry = 1_000
    let broadInvalidationScheduled = false
    let celebrationTimer: number | undefined

    const scheduleBroadInvalidation = () => {
      if (broadInvalidationScheduled) return
      broadInvalidationScheduled = true
      queueMicrotask(() => {
        broadInvalidationScheduled = false
        if (controller.signal.aborted) return
        void queryClient.invalidateQueries({ queryKey: ["family", activeFamilyId] })
        void queryClient.invalidateQueries({ queryKey: ["v1", activeFamilyId] })
        void queryClient.invalidateQueries({ queryKey: ["families"] })
        void queryClient.invalidateQueries({ queryKey: ["notifications"] })
      })
    }

    const invalidateFor = (event: Envelope) => {
      if (event.type === "family.changed" || event.type === "family.invalidate") {
        scheduleBroadInvalidation()
        return
      }
      for (const key of relatedKeys(event.type)) {
        void queryClient.invalidateQueries({ queryKey: ["family", activeFamilyId, ...key] })
      }
      void queryClient.invalidateQueries({ queryKey: ["v1", activeFamilyId] })
      // Every streamed domain event is audited in the same transaction.
      void queryClient.invalidateQueries({ queryKey: ["family", activeFamilyId, "audit"] })
      void queryClient.invalidateQueries({ queryKey: ["notifications"] })
      const celebrationText = celebrationFor(event.type)
      if (celebrationText) {
        setCelebration(celebrationText)
        if (celebrationTimer) window.clearTimeout(celebrationTimer)
        celebrationTimer = window.setTimeout(() => setCelebration(null), 2_800)
      }
      if (event.type.startsWith("penalty.")) {
        void queryClient.invalidateQueries({ queryKey: ["family", activeFamilyId, "currency"] })
      }
      if (event.type.startsWith("notification.")) {
        window.dispatchEvent(new CustomEvent("family-coin:notification", { detail: event.payload }))
      }
      if (
        event.type.startsWith("member.") ||
        event.type.startsWith("invitation.") ||
        event.type.startsWith("family.member.") ||
        event.type.startsWith("family.invitation.")
      ) {
        void queryClient.invalidateQueries({ queryKey: ["families"] })
      }
    }

    const connect = async () => {
      while (!controller.signal.aborted) {
        if (!navigator.onLine) {
          setState("offline")
          await new Promise((resolve) => window.setTimeout(resolve, retry))
          continue
        }
        setState("connecting")
        try {
          const token = getAccessToken() ?? (await refreshSession()).accessToken
          const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""
          const response = await fetch(getApiUrl(`/families/${activeFamilyId}/events${query}`), {
            headers: { Accept: "text/event-stream", Authorization: `Bearer ${token}` },
            credentials: "include",
            signal: controller.signal
          })
          if (response.status === 401) {
            await refreshSession()
            continue
          }
          if (response.status === 403) {
            setState("offline")
            await queryClient.invalidateQueries({ queryKey: ["families"] })
            return
          }
          if (!response.ok || !response.body) throw new Error(`SSE ${response.status}`)
          setState("live")
          retry = 1_000

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ""
          while (!controller.signal.aborted) {
            const chunk = await reader.read()
            if (chunk.done) break
            buffer += decoder.decode(chunk.value, { stream: true }).replace(/\r\n/g, "\n")
            let boundary = buffer.indexOf("\n\n")
            while (boundary >= 0) {
              const event = parseEvent(buffer.slice(0, boundary))
              buffer = buffer.slice(boundary + 2)
              if (event) {
                cursor = event.sequence ?? event.id ?? cursor
                if (cursor) saveCursor(user.id, activeFamilyId, cursor)
                invalidateFor(event)
              }
              boundary = buffer.indexOf("\n\n")
            }
          }
        } catch {
          if (controller.signal.aborted) return
          setState(navigator.onLine ? "connecting" : "offline")
          await new Promise((resolve) => window.setTimeout(resolve, retry))
          retry = Math.min(retry * 2, 30_000)
        }
      }
    }

    void connect()
    return () => {
      controller.abort()
      if (celebrationTimer) window.clearTimeout(celebrationTimer)
    }
  }, [activeFamilyId, queryClient, user])

  const value = useMemo(() => ({ state }), [state])
  return (
    <RealtimeContext.Provider value={value}>
      {children}
      {celebration ? (
        <div className="celebration-event" role="status" aria-live="polite">
          <span aria-hidden="true">✨</span>
          <strong>{celebration}</strong>
        </div>
      ) : null}
    </RealtimeContext.Provider>
  )
}

function celebrationFor(type: string) {
  if (type === "achievement.awarded") return "Новая ачивка!"
  if (type === "ability.granted") return "Новая суперспособность!"
  if (type === "task.rewarded") return "Награда получена!"
  if (type === "shop.purchased") return "Покупка в семейном магазине!"
  return null
}

export function useRealtime() {
  return useContext(RealtimeContext)
}
